import {Component, OnInit} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {first, map, switchMap} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {AssetInput, Asset } from "../../../mgmt-api-client/model";
import {AssetService} from "../../../mgmt-api-client";
import {AssetEditorDialog} from "../asset-editor-dialog/asset-editor-dialog.component";
import {ConfirmationDialogComponent, ConfirmDialogModel} from "../confirmation-dialog/confirmation-dialog.component";
import {NotificationService} from "../../services/notification.service";
import {AssetDetail} from "../asset-detail/asset-detail.component";
import { JsonLdObject } from '@think-it-labs/edc-connector-client';


@Component({
  selector: 'edc-demo-asset-viewer',
  templateUrl: './asset-viewer.component.html',
  styleUrls: ['./asset-viewer.component.scss']
})
export class AssetViewerComponent implements OnInit {

  filteredAssets$: Observable<Asset[]> = of([]);
  searchText = '';
  isTransferring = false;
  private fetch$ = new BehaviorSubject(null);

  constructor(private assetService: AssetService,
              private notificationService: NotificationService,
              private readonly dialog: MatDialog) {
  }

  private showError(error: string, errorMessage: string) {
    this.notificationService.showError(errorMessage);
    console.error(error);
  }

  ngOnInit(): void {
    this.filteredAssets$ = this.fetch$
      .pipe(
        switchMap(() => {
          const assets$ = this.assetService.requestAssets();
          return !!this.searchText
            ? assets$.pipe(map(assets => assets.filter(asset => asset.properties.optionalValue<string>('edc', 'name')?.includes(this.searchText))))
            : assets$;
        }));
  }

  isBusy() {
    return this.isTransferring;
  }

  onSearch() {
    this.fetch$.next(null);
  }

  onDelete(asset: Asset) {
    const dialogData = ConfirmDialogModel.forDelete("asset", `"${asset.id}"`)
    const ref = this.dialog.open(ConfirmationDialogComponent, {maxWidth: "120%", data: dialogData});

    ref.afterClosed().subscribe({
      next: res => {
        if (res) {
          this.assetService.removeAsset(asset.id).subscribe({
            next: () => this.fetch$.next(null),
            error: err => this.showError(err, "This asset cannot be deleted"),
            complete: () => this.notificationService.showInfo("Successfully deleted")
          });
        }
      }
    });
  }

  onCreate() {
    const asset: AssetInput = {
      "@id": "",
      properties: {},
      dataAddress: {"type": ""}
    };
    const dialogRef = this.dialog.open(AssetEditorDialog, {data: asset, disableClose: true});
    dialogRef.afterClosed().pipe(first()).subscribe((result: { assetInput?: AssetInput }) => {
      const newAsset = result?.assetInput;
      if (newAsset) {
        this.assetService.createAsset(newAsset).subscribe({
          next: ()=> this.fetch$.next(null),
          error: err => this.showError(err, "This asset cannot be created"),
          complete: () => this.notificationService.showInfo("Successfully created"),
        })
      }
    })  
  }

  // Added By Nri 2025.3.6 Start
  onUpdate(assetInfo: Asset) {    
    const asset: AssetInput = {
      "@id": assetInfo.id,
      properties: assetInfo.properties,
      dataAddress: {
        "type": assetInfo.dataAddress.optionalValue('edc','type') || '',
        "name": assetInfo.dataAddress.optionalValue('edc','name'),
        "baseUrl": assetInfo.dataAddress.optionalValue('edc','baseUrl')
      }
    };
    const dialogRef = this.dialog.open(AssetEditorDialog, {data: asset, disableClose: true});
    dialogRef.afterClosed().pipe(first()).subscribe((result: { assetInput?: AssetInput }) => {
      const newAsset = result?.assetInput;
      if (newAsset) {
        this.assetService.updateAsset(newAsset).subscribe({
          next: ()=> this.fetch$.next(null),
          error: err => this.showError(err, "This asset cannot be updated"),
          complete: () => this.notificationService.showInfo("Successfully updated"),
        })
      }
    })  
  }

  onShowDetail(asset: Asset) {
    this.dialog.open(AssetDetail, {data: asset});
  }
  // Added By Nri 2025.3.6 End
  getOptionalValue(types: JsonLdObject): any {
    if (types && Array.isArray(types) && types.length > 0) {
      const firstType = types[0];
      if (firstType && typeof firstType === 'object' && '@id' in firstType) {
        return this.processString(firstType['@id']);
      }
    }
  }

  processString(value: string): string {
    if (typeof value !== 'string') {
      return value; 
    }

    return value
      .replace('http://bmwgroup.com/catenax/taxonomy#', '')
      .replace('https://w3id.org/catenax/taxonomy#', '')
      .replace('EcuCertificateSigningRequestDataPushFeedbackNotification', 'EcuCSRDataPushFeedbackNotification')
      .replace('EcuCertificateSigningRequestDataPushNotification', 'EcuCSRDataPushNotification')
  }

}
